import {usersApi} from '@/plugins/axios';
import {useAuthStore} from '@/stores/auth-store';
import {DEFAULT_LANGUAGE, DEFAULT_LANGUAGES} from '@/utils/constants/general-constants';
import {i18n} from '@/plugins/i18n';
import {BaseResponse} from '@/interfaces/base-response.interface';

export const setToken = () => {
  const authStore = useAuthStore();
  const token = authStore.token || null;
  let lang = i18n.global.locale.value || 'en';

  if (!DEFAULT_LANGUAGES.includes(lang)) lang = DEFAULT_LANGUAGE;
  usersApi.defaults.headers.common['x-access-lang'] = lang;

  if (token) usersApi.defaults.headers['authorization'] = token;
  else delete usersApi.defaults.headers['authorization'];
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getErrorCatch = (error: any) => {
  const ret = {
    error: 'Error desconocido.',
    status: null,
    errors: [],
  };

  if (error?.response) {
    console.error('error', error);
    const {data, status} = error.response;
    if (status) ret.status = status;

    if (status === 401) ret.error = data.err || 'Unauthorized!';
    else if (data) {
      ret.error = data.err || error.toString() || 'Unknown error!';
      if (data.errors) {
        ret.errors = data.errors;
        ret.error = data.errors[0].message || error.toString() || 'Unknown error!'; // if exist other data assignate;
      }
    } else ret.error = data.err || error?.toString() || 'Unknown error!';
  } else {
    ret.error = error?.toString() || 'Unknown error!';
  }

  return ret;
};

/**
 * @function getData
 * @param {String} endpoint Endpoint to request.
 * @param params
 * @return {Object} response Response data or error.
 */
export const getData = async <T>(endpoint: string, params = {}): Promise<BaseResponse<T>> => {
  const controller = new AbortController();
  const {signal} = controller;
  try {
    setToken();
    const res = await usersApi.get(endpoint, {params, signal});
    return {success: true, data: res.data.data, status: res.status};
  } catch (e) {
    controller.abort();
    return {success: false, ...getErrorCatch(e)};
  }
};

/**
 * @function getData
 * @param {String} endpoint Endpoint to request.
 * @return {Object} response Response data or error.
 */
export const getDataContent = async <T>(endpoint: string): Promise<BaseResponse<T>> => {
  try {
    return await usersApi.get(endpoint);
  } catch (e) {
    return {success: false, ...getErrorCatch(e)};
  }
};

/**
 * PostData
 * @param endpoint
 * @param data
 */
export const postData = async <T>(endpoint: string, data = {}): Promise<BaseResponse<T>> => {
  const controller = new AbortController();
  const {signal} = controller;
  try {
    setToken();
    const res = await usersApi.post(endpoint, data, {signal});
    return {
      success: true,
      data: res.data.data ?? res.data.msg,
      msg: res.data.msg,
      status: res.status,
    };
  } catch (e) {
    controller.abort();
    return {success: false, ...getErrorCatch(e)};
  }
};

/**
 *
 * @param endpoint
 * @param data
 */
export const putData = async <T>(endpoint: string, data = {}): Promise<BaseResponse<T>> => {
  const controller = new AbortController();
  const {signal} = controller;
  try {
    setToken();
    const res = await usersApi.put(endpoint, data, {signal});
    return {success: true, data: res.data.data, msg: res.data.msg, status: res.status};
  } catch (e) {
    controller.abort();
    return {success: false, ...getErrorCatch(e)};
  }
};

/**
 *
 * @param endpoint
 * @param data
 */
export const patchData = async <T>(endpoint: string, data = {}): Promise<BaseResponse<T>> => {
  const controller = new AbortController();
  const {signal} = controller;
  try {
    setToken();
    const res = await usersApi.patch(endpoint, data, {signal});
    return {success: true, data: res.data.data, msg: res.data.msg, status: res.status};
  } catch (e) {
    controller.abort();
    return {success: false, ...getErrorCatch(e)};
  }
};

/**
 * @function deleteData
 * @param {String} endpoint Endpoint to request.
 * @param data
 * @return {Object} response Response data or error.
 */
export const deleteData = async <T>(endpoint: string, data = {}): Promise<BaseResponse<T>> => {
  const controller = new AbortController();
  const {signal} = controller;
  try {
    setToken();
    const res = await usersApi.delete(endpoint, {signal, data});
    return {success: true, data: res.data.data, msg: res.data.msg, status: res.status};
  } catch (e) {
    controller.abort();
    return {success: false, ...getErrorCatch(e)};
  }
};

export const getFileData = async <T>(endpoint: string, params = {}): Promise<BaseResponse<T>> => {
  const controller = new AbortController();
  const {signal} = controller;
  try {
    setToken();
    const res = await usersApi.get(endpoint, {params, signal, responseType: 'blob'});
    return {success: true, data: res.data};
  } catch (e) {
    controller.abort();
    return {success: false, ...getErrorCatch(e)};
  }
};

export const postFileData = async <T>(
  endpoint: string,
  data = {},
  params = {},
): Promise<BaseResponse<T>> => {
  const controller = new AbortController();
  const {signal} = controller;
  try {
    setToken();
    const res = await usersApi.post(endpoint, data, {params, signal, responseType: 'blob'});
    return {success: true, data: res.data};
  } catch (e) {
    controller.abort();
    return {success: false, ...getErrorCatch(e)};
  }
};
